import React, { useMemo } from 'react';
import ptBr from 'date-fns/locale/pt-BR';
import { registerLocale } from 'react-datepicker';

import { FilterPeriodType } from 'utils/constants';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUtilityCompanies } from 'store/utility_company/utility_company.selector';
import { selectEstablishments } from 'store/establishment/establishment.selector';
import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';
import {
  selectFilterEndDate,
  selectFilterMonthSelected,
  selectFilterPeriod,
  selectFilterStartDate,
  selectFilterState,
  selectFilterUtilityCompanyId,
} from 'store/filter_consumer_unit/filter_consumer_unit.selector';
import {
  setFilterEndDate,
  setFilterMonthSelected,
  setFilterPeriod,
  setFilterStartDate,
  setFilterState,
  setFilterUtilityCompanyId,
} from 'store/filter_consumer_unit/filter_consumer_unit.slice';

registerLocale('pt-BR', ptBr);

import {
  ConsumerUnitFilterContainer,
  Wrapper,
  Label,
  LabelContainer,
  Select,
  DateSelector,
} from './filter.styles';

export const Filter = () => {
  const dispatch = useAppDispatch();

  const allUtilityCompanies = useAppSelector(selectUtilityCompanies);
  const allEstablishments = useAppSelector(selectEstablishments);
  const consumerUnitList = useAppSelector(selectConsumerUnitList);

  // Só oferece como filtro os tipos de estabelecimento e distribuidoras que o
  // usuário realmente tem entre suas UCs — não faz sentido listar "Elektro" se
  // nenhuma UC é da Elektro. Deriva os IDs presentes na lista de UCs (que já
  // exclui as ocultas por plano).
  const usedEstablishmentIds = useMemo(
    () =>
      new Set(
        (consumerUnitList ?? [])
          .map((uc) => uc.establishment?.id)
          .filter((id): id is string => !!id)
      ),
    [consumerUnitList]
  );
  const usedUtilityCompanyIds = useMemo(
    () =>
      new Set(
        (consumerUnitList ?? [])
          .map((uc) => uc.utilityCompany?.id)
          .filter((id): id is string => !!id)
      ),
    [consumerUnitList]
  );

  const establishments = useMemo(
    () =>
      (allEstablishments ?? []).filter((e) => usedEstablishmentIds.has(e.id)),
    [allEstablishments, usedEstablishmentIds]
  );
  const utilityCompanies = useMemo(
    () =>
      (allUtilityCompanies ?? []).filter((u) =>
        usedUtilityCompanyIds.has(u.id)
      ),
    [allUtilityCompanies, usedUtilityCompanyIds]
  );

  const filterState = useAppSelector(selectFilterState);
  const filterUtilityCompanyId = useAppSelector(selectFilterUtilityCompanyId);
  const filterPeriod = useAppSelector(selectFilterPeriod);
  const filterMonthSelected = useAppSelector(selectFilterMonthSelected);
  const filterStartDate = useAppSelector(selectFilterStartDate);
  const filterEndDate = useAppSelector(selectFilterEndDate);

  const handleChangePeriod = ([newStartDate, newEndDate]: [
    Date | null,
    Date | null
  ]) => {
    dispatch(setFilterStartDate({ startDate: newStartDate }));
    dispatch(setFilterEndDate({ endDate: newEndDate }));
  };

  return (
    <ConsumerUnitFilterContainer className="mb-4 pb-2">
      <Wrapper>
        {/* <LabelContainer className="w-25">
          <Label className="mb-2">&nbsp;</Label>
          <TextInputContainer>
            <TextInput
              type="text"
              id="name"
              name="name"
              placeholder="| Pesquisar"
              className="ps-3"
            />
            <Button>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M11.7422 10.3439C12.5329 9.2673 13 7.9382 13 6.5C13 2.91015 10.0899 0 6.5 0C2.91015 0 0 2.91015 0 6.5C0 10.0899 2.91015 13 6.5 13C7.93858 13 9.26801 12.5327 10.3448 11.7415L10.3439 11.7422C10.3734 11.7822 10.4062 11.8204 10.4424 11.8566L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L11.8566 10.4424C11.8204 10.4062 11.7822 10.3734 11.7422 10.3439ZM12 6.5C12 9.53757 9.53757 12 6.5 12C3.46243 12 1 9.53757 1 6.5C1 3.46243 3.46243 1 6.5 1C9.53757 1 12 3.46243 12 6.5Z"
                  fill="#152E63"
                />
              </svg>
            </Button>
          </TextInputContainer>
        </LabelContainer> */}
        {establishments && establishments.length > 0 && (
          <LabelContainer className="w-25">
            <Label className="mb-2">Tipo de estabelecimento</Label>
            <Select
              className="ps-2"
              value={filterState}
              onChange={(e) =>
                dispatch(setFilterState({ filterState: e.target.value }))
              }
            >
              <option key="all" value="all">
                Todos
              </option>
              {establishments.map((est) => (
                <option key={est.id} value={est.id}>
                  {est.name}
                </option>
              ))}
            </Select>
          </LabelContainer>
        )}
        <LabelContainer className="w-25">
          <Label className="mb-2">Distribuidora</Label>
          <Select
            className="ps-2"
            value={filterUtilityCompanyId}
            onChange={(e) =>
              dispatch(
                setFilterUtilityCompanyId({ utilityCompanyId: e.target.value })
              )
            }
          >
            <option key="all" value="all">
              Todas
            </option>
            {utilityCompanies?.map((uc) => (
              <option key={uc.id} value={uc.id}>
                {uc.name}
              </option>
            ))}
          </Select>
        </LabelContainer>
        <LabelContainer className="w-25">
          <Label className="mb-2">Período</Label>
          <Select
            className="ps-2"
            value={filterPeriod}
            onChange={(e) =>
              dispatch(
                setFilterPeriod({
                  period: e.target
                    .value as (typeof FilterPeriodType)[keyof typeof FilterPeriodType],
                })
              )
            }
          >
            <option value={FilterPeriodType.CURRENT_MONTH}>Mês corrente</option>
            <option value={FilterPeriodType.SPECIFIC_MONTH}>
              Mês específico
            </option>
            <option value={FilterPeriodType.CURRENT_YEAR}>Ano corrente</option>
            <option value={FilterPeriodType.LAST_YEAR}>Último ano</option>
            <option value={FilterPeriodType.LAST_TWELVE_MONTHS}>
              Últimos 12 meses
            </option>
            <option value={FilterPeriodType.SPECIFIC_PERIOD}>
              Período específico
            </option>
          </Select>
        </LabelContainer>
        {filterPeriod === FilterPeriodType.SPECIFIC_MONTH && (
          <LabelContainer className="w-25">
            <Label className="mb-2">Selecionar mês</Label>
            <DateSelector
              selected={filterMonthSelected}
              onChange={(date) =>
                dispatch(
                  setFilterMonthSelected({ monthSelected: date as Date })
                )
              }
              dateFormat="MM/yyyy"
              locale="pt-BR"
              showMonthYearPicker
              calendarClassName="calendar-body-selector"
              maxDate={new Date()}
            />
          </LabelContainer>
        )}
        {filterPeriod === FilterPeriodType.SPECIFIC_PERIOD && (
          <LabelContainer className="w-25">
            <Label className="mb-2">Selecionar período</Label>
            <DateSelector
              selected={filterStartDate}
              onChange={handleChangePeriod}
              selectsRange
              startDate={filterStartDate}
              endDate={filterEndDate}
              dateFormat="MM/yyyy"
              locale="pt-BR"
              showMonthYearPicker
              calendarClassName="calendar-body-selector"
              maxDate={new Date()}
            />
          </LabelContainer>
        )}
      </Wrapper>
    </ConsumerUnitFilterContainer>
  );
};
